import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "../utils/analytics";
import "./FooterContactForm.css";

export interface FooterContactFormProps {
  /** `footer`, site footer; `mobile`, compact mobile styling */
  variant?: "footer" | "mobile";
}

/** Footer enquiry select. Tags in GHL are the option labels, not Program:/Goal: prefixes. Early bird applies both event tags. */
type FooterEnquiryType = "" | "early-bird" | "general" | "sponsor" | "speaker" | "events" | "media";

const FooterContactForm: React.FC<FooterContactFormProps> = ({ variant = "footer" }) => {
  const attribution = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const readParam = (key: string) => params.get(key) || "";
    return {
      sourceUrl: window.location.href,
      referrer: document.referrer || "",
      utmSource: readParam("utm_source"),
      utmMedium: readParam("utm_medium"),
      utmCampaign: readParam("utm_campaign"),
      utmTerm: readParam("utm_term"),
      utmContent: readParam("utm_content"),
    };
  }, []);

  const [enquiryType, setEnquiryType] = useState<FooterEnquiryType>("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [interestSydney, setInterestSydney] = useState(false);
  const [interestLondon, setInterestLondon] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const rootClass = `footer-contact-form footer-contact-form--${variant}`.trim();

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!enquiryType) next.enquiryType = "Please choose an enquiry type";
    if (!firstName.trim()) next.firstName = "Required";
    if (!lastName.trim()) next.lastName = "Required";
    if (!email.trim()) next.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Invalid email";
    if (!message.trim()) next.message = "Required";
    if (!agreeToTerms) next.agreeToTerms = "Please accept the terms to continue";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus("idle");
    setSubmitMessage("");
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          message: message.trim(),
          agreeToTerms,
          subscribeToNewsletter,
          simpleContact: true,
          preferredContact: "email",
          enquiryType,
          eventCities: [
            ...(interestSydney ? (["sydney"] as const) : []),
            ...(interestLondon ? (["london"] as const) : []),
          ],
          ...attribution,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSubmitStatus("success");
        setSubmitMessage(typeof data.message === "string" ? data.message : "Thanks, we will be in touch.");
        setEnquiryType("");
        setFirstName("");
        setLastName("");
        setEmail("");
        setMessage("");
        setInterestSydney(false);
        setInterestLondon(false);
        setAgreeToTerms(false);
        setSubscribeToNewsletter(false);
        setErrors({});
        trackEvent("submit", "form", "footer-register", 1);
      } else {
        setSubmitStatus("error");
        setSubmitMessage(typeof data.error === "string" ? data.error : "Something went wrong. Please try again.");
        trackEvent("error", "form", "footer-register-error", 0);
      }
    } catch {
      setSubmitStatus("error");
      setSubmitMessage("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const enquiryId = `fcf-enquiry-${variant}`;

  return (
    <form className={rootClass} onSubmit={handleSubmit} noValidate aria-label="Register interest">
      <div className="footer-contact-form__field">
        <label className="footer-contact-form__label" htmlFor={enquiryId}>
          Enquiry type <span aria-hidden="true">*</span>
        </label>
        <select
          id={enquiryId}
          name="enquiryType"
          value={enquiryType}
          onChange={(ev) => {
            setEnquiryType(ev.target.value as FooterEnquiryType);
            if (errors.enquiryType) setErrors((p) => ({ ...p, enquiryType: "" }));
          }}
          className={[
            "footer-contact-form__select",
            "footer-contact-form__input",
            enquiryType === "early-bird" ? "is-early-bird" : "",
            errors.enquiryType ? "is-invalid" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <option value="">Select enquiry type</option>
          <option value="early-bird">Early bird</option>
          <option value="general">General enquiry</option>
          <option value="sponsor">Sponsorship / partnership</option>
          <option value="speaker">Speaking / contribution</option>
          <option value="events">Events</option>
          <option value="media">Media / press</option>
        </select>
        {errors.enquiryType ? <span className="footer-contact-form__err">{errors.enquiryType}</span> : null}
      </div>

      <div className="footer-contact-form__row">
        <div className="footer-contact-form__field">
          <label className="footer-contact-form__label" htmlFor={`fcf-fn-${variant}`}>
            First name <span aria-hidden="true">*</span>
          </label>
          <input
            id={`fcf-fn-${variant}`}
            name="firstName"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(ev) => {
              setFirstName(ev.target.value);
              if (errors.firstName) setErrors((p) => ({ ...p, firstName: "" }));
            }}
            className={errors.firstName ? "footer-contact-form__input is-invalid" : "footer-contact-form__input"}
          />
          {errors.firstName ? <span className="footer-contact-form__err">{errors.firstName}</span> : null}
        </div>

        <div className="footer-contact-form__field">
          <label className="footer-contact-form__label" htmlFor={`fcf-ln-${variant}`}>
            Last name <span aria-hidden="true">*</span>
          </label>
          <input
            id={`fcf-ln-${variant}`}
            name="lastName"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(ev) => {
              setLastName(ev.target.value);
              if (errors.lastName) setErrors((p) => ({ ...p, lastName: "" }));
            }}
            className={errors.lastName ? "footer-contact-form__input is-invalid" : "footer-contact-form__input"}
          />
          {errors.lastName ? <span className="footer-contact-form__err">{errors.lastName}</span> : null}
        </div>
      </div>

      <div className="footer-contact-form__field">
        <label className="footer-contact-form__label" htmlFor={`fcf-em-${variant}`}>
          Email <span aria-hidden="true">*</span>
        </label>
        <input
          id={`fcf-em-${variant}`}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(ev) => {
            setEmail(ev.target.value);
            if (errors.email) setErrors((p) => ({ ...p, email: "" }));
          }}
          className={errors.email ? "footer-contact-form__input is-invalid" : "footer-contact-form__input"}
        />
        {errors.email ? <span className="footer-contact-form__err">{errors.email}</span> : null}
      </div>

      <div className="footer-contact-form__field">
        <label className="footer-contact-form__label" htmlFor={`fcf-msg-${variant}`}>
          Message <span aria-hidden="true">*</span>
        </label>
        <textarea
          id={`fcf-msg-${variant}`}
          name="message"
          rows={variant === "mobile" ? 3 : 4}
          value={message}
          onChange={(ev) => {
            setMessage(ev.target.value);
            if (errors.message) setErrors((p) => ({ ...p, message: "" }));
          }}
          className={errors.message ? "footer-contact-form__textarea is-invalid" : "footer-contact-form__textarea"}
          placeholder="Events, partnerships, or a quick hello"
        />
        {errors.message ? <span className="footer-contact-form__err">{errors.message}</span> : null}
      </div>

      <fieldset className="footer-contact-form__events">
        <legend className="footer-contact-form__label">Events of interest</legend>
        <p className="footer-contact-form__events-hint">Sydney, London, both, or neither — optional</p>
        <div className="footer-contact-form__event-choices">
          <label className="footer-contact-form__check">
            <input
              type="checkbox"
              name="eventSydney"
              checked={interestSydney}
              onChange={(ev) => setInterestSydney(ev.target.checked)}
            />
            <span>Sydney</span>
          </label>
          <label className="footer-contact-form__check">
            <input
              type="checkbox"
              name="eventLondon"
              checked={interestLondon}
              onChange={(ev) => setInterestLondon(ev.target.checked)}
            />
            <span>London</span>
          </label>
        </div>
      </fieldset>

      <p className="footer-contact-form__fineprint">
        We use this to respond to your enquiry. You can ask to be removed from follow-ups at any time. See our{" "}
        <Link to="/privacy">privacy policy</Link>.
      </p>

      <label className="footer-contact-form__check">
        <input
          type="checkbox"
          checked={subscribeToNewsletter}
          onChange={(ev) => setSubscribeToNewsletter(ev.target.checked)}
        />
        <span>Email me occasional updates and announcements</span>
      </label>

      <label className="footer-contact-form__check">
        <input
          type="checkbox"
          checked={agreeToTerms}
          onChange={(ev) => {
            setAgreeToTerms(ev.target.checked);
            if (errors.agreeToTerms) setErrors((p) => ({ ...p, agreeToTerms: "" }));
          }}
          className={errors.agreeToTerms ? "is-invalid" : undefined}
        />
        <span>
          I agree to the{" "}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">
            terms
          </Link>{" "}
          <span aria-hidden="true">*</span>
        </span>
      </label>
      {errors.agreeToTerms ? <span className="footer-contact-form__err footer-contact-form__err--block">{errors.agreeToTerms}</span> : null}

      <button type="submit" className="footer-contact-form__submit" disabled={isSubmitting}>
        {isSubmitting ? "Sending…" : "Submit"}
      </button>

      {submitMessage ? (
        <p className={`footer-contact-form__feedback footer-contact-form__feedback--${submitStatus}`} role="status">
          {submitMessage}
        </p>
      ) : null}
    </form>
  );
};

export default FooterContactForm;
